import { Link } from "react-router-dom";
import {
  Check, AlertTriangle, Monitor, ShoppingCart, Users, Briefcase,
  RefreshCw, Shield, Database, Activity, FileText, Headphones,
  Server, Zap, ArrowRight, ChevronRight, Clock, FormInput, Gauge, Lock, UserX, KeyRound
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import HeroDashboard from "@/components/HeroDashboard";
import ClientsCarousel from "@/components/ClientsCarousel";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const problems = [
  { icon: Clock, text: "Update-uri făcute rar sau deloc" },
  { icon: AlertTriangle, text: "Teamă că site-ul se strică după un update" },
  { icon: FormInput, text: "Formulare care nu mai trimit cereri" },
  { icon: Gauge, text: "Site lent sau instabil" },
  { icon: Database, text: "Lipsă de backup real" },
  { icon: Lock, text: "Risc de malware sau vulnerabilități" },
  { icon: UserX, text: "Dependență de un singur developer" },
  { icon: KeyRound, text: "Lipsă de claritate asupra accesurilor" },
];

const audiences = [
  {
    icon: Monitor,
    title: "Site-uri de prezentare",
    text: "Pentru firme care vor un site actualizat, sigur și îngrijit corect, fără bătăi de cap pentru fiecare ajustare minoră.",
  },
  {
    icon: Briefcase,
    title: "Site-uri business care generează lead-uri",
    text: "Pentru companii care folosesc site-ul ca instrument real de business și nu își permit formulare nefuncționale, erori sau perioade lungi fără suport.",
  },
  {
    icon: ShoppingCart,
    title: "Magazine online WooCommerce",
    text: "Pentru magazine care depind direct de site pentru venituri și au nevoie de monitorizare mai atentă, backup-uri mai dese și prioritate crescută la incidente.",
  },
  {
    icon: Users,
    title: "Fără echipă tehnică internă",
    text: "Pentru companii care au nevoie de un partener responsabil, ușor de contactat și capabil să țină site-ul sub control.",
  },
];

const benefits = [
  "Actualizări WordPress, pluginuri și temă gestionate proactiv",
  "Monitorizare uptime",
  "Verificări malware zilnice",
  "Backup-uri automate",
  "Verificări funcționale de bază",
  "Suport pentru incidente și intervenții minore",
  "Raport lunar",
  "Recomandări pentru stabilitate și performanță",
  "Restaurare backup în caz de incident",
];

const differentiators = [
  {
    title: "Nu doar reacție, ci prevenție",
    text: "Monitorizăm și întreținem proactiv, ca să reducem riscul problemelor evitabile.",
  },
  {
    title: "Fără lock-in inutil",
    text: "Credem în transparență, accesuri clare și relații curate, nu în dependență artificială.",
  },
  {
    title: "Suport uman și răspuns clar",
    text: "Când apare o problemă, nu vrei o platformă complicată. Vrei să știi cine se ocupă și ce urmează.",
  },
  {
    title: "Potrivit pentru business-uri reale",
    text: "Nu construim o ofertă doar pentru site-uri hobby, ci pentru companii care au nevoie de stabilitate și control.",
  },
];

const pricingPlans = [
  {
    name: "Site de Prezentare",
    price: "250 lei",
    period: "+ TVA / lună",
    text: "Pentru site-uri WordPress de prezentare și companii care vor mentenanță corectă, monitorizare și modificări minore de conținut incluse.",
    cta: "Vezi detalii",
    href: "/pachete",
    featured: false,
  },
  {
    name: "WooCommerce",
    price: "de la 550 lei",
    period: "+ TVA / lună",
    text: "Pentru magazine online care au nevoie de backup-uri mai dese, verificări operaționale suplimentare și reacție rapidă atunci când apar probleme.",
    badge: "Recomandat pentru magazine online",
    cta: "Vezi detalii",
    href: "/pachete",
    featured: true,
  },
  {
    name: "Custom / High Traffic",
    price: "Ofertă personalizată",
    period: "",
    text: "Pentru proiecte cu trafic mare, integrări complexe sau cerințe avansate de infrastructură și suport.",
    cta: "Cere o ofertă",
    href: "/contact",
    featured: false,
  },
];

const steps = [
  { num: "01", title: "Audit inițial", text: "Analizăm site-ul, configurația actuală, riscurile vizibile și ce pachet ți se potrivește." },
  { num: "02", title: "Onboarding și accesuri", text: "Preluăm accesurile necesare, verificăm backup-ul, hostingul și punctele critice." },
  { num: "03", title: "Monitorizare și mentenanță", text: "Gestionăm actualizările, verificările și intervențiile recurente, cu raportare clară." },
  { num: "04", title: "Suport și îmbunătățire continuă", text: "Când apare o problemă sau o solicitare importantă, răspundem clar și intervenim organizat." },
];

const faqItems = [
  { q: "Lucrați doar cu WordPress?", a: "Da. Serviciul este construit special pentru WordPress și WooCommerce." },
  { q: "Faceți și modificări pe site?", a: "Da, toate planurile includ modificări minore de conținut. Sunt incluse până la 30 de minute de lucru pe lună pentru ajustări simple, precum actualizare text, înlocuire imagine sau mici modificări de prezentare. Modificările mai ample se estimează și se facturează separat." },
  { q: "Dacă update-ul strică site-ul?", a: "Actualizările sunt gestionate controlat, iar dacă apare un incident intervenim și restaurăm din backup atunci când este necesar." },
  { q: "Oferiți și infrastructură?", a: "Da, pentru proiectele potrivite putem oferi și infrastructură managed sau soluții dedicate." },
  { q: "Ce se întâmplă la WooCommerce?", a: "Magazinele online beneficiază de verificări suplimentare, backup-uri mai dese și prioritate crescută la incidente." },
];

export default function HomePage() {
  usePageMeta(
    "Ordell WebCare - Mentenanță și Suport WordPress",
    "Mentenanță și suport WordPress pentru site-uri de prezentare și magazine WooCommerce. Actualizări, backup-uri, monitorizare, securitate și intervenții rapide."
  );

  return (
    <>
      <JsonLd page="home" />
      {/* HERO */}
      <section data-testid="hero-section" className="py-16 lg:py-28 overflow-hidden">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
                Ai grijă de business.<br />
                <span className="text-[#0F2942]">Noi avem grijă de WordPress.</span>
              </h1>
              <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4 max-w-xl">
                Mentenanță și suport WordPress pentru site-uri de prezentare și magazine WooCommerce care au nevoie de stabilitate, securitate și intervenții rapide atunci când contează.
              </p>
              <p className="text-sm text-[#4A5568] leading-relaxed mb-8 max-w-xl">
                Ținem site-ul actualizat, monitorizat și gestionat corect, fără stres, fără blocaje și fără dependență de un singur developer.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all text-center flex items-center justify-center gap-2"
                >
                  Cere audit gratuit <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/pachete"
                  data-testid="hero-cta-secondary"
                  className="bg-white text-[#0F2942] border border-[#0F2942]/10 font-medium px-6 py-3 rounded-xl hover:bg-[#F0F5F4] transition-all text-center"
                >
                  Vezi pachetele
                </Link>
              </div>
              <p className="text-xs text-[#64748B] max-w-md">
                Îți spunem clar ce riscuri vedem și ce plan ți se potrivește.
              </p>
            </div>
            <div className="animate-fade-in-up-delay-2 lg:pl-8">
              <HeroDashboard />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section data-testid="problem-section" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <div className="max-w-3xl">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
              Site-ul tău nu ar trebui să fie o sursă constantă de stres.
            </h2>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
              Site-ul WordPress este important pentru imagine, lead-uri sau vânzări, dar în multe companii este întreținut doar când apare o problemă. Update-urile se amână, backup-urile nu sunt verificate, developerul răspunde greu, iar când ceva se strică, toată lumea intră în panică.
            </p>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-8">
              Nu știi exact ce accesuri ai, nu știi dacă există backup real și nu știi cine preia responsabilitatea.
            </p>
            <p className="text-sm font-medium text-[#0F2942] mb-6">
              Mentenanța trebuie să însemne ordine, responsabilitate și claritate.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {problems.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#0F2942]/5">
                <p.icon className="w-4 h-4 text-[#0F2942]/40 mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]/80">{p.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS CAROUSEL */}
      <ClientsCarousel />

      {/* AUDIENCE */}
      <section data-testid="audience-section" className="py-20 lg:py-28">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-12 max-w-2xl">
            Un serviciu creat pentru business-uri care depind de site-ul lor.
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <div
                key={i}
                data-testid={`audience-card-${i}`}
                className="bg-white rounded-2xl p-8 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-4">
                  <a.icon className="w-5 h-5 text-[#1A4A40]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-3">{a.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES INCLUDED */}
      <section data-testid="services-section" className="py-20 lg:py-28 bg-white">
        <div className="section-container">
          <div className="max-w-3xl mb-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
              Mai mult decât update-uri. Grijă operațională continuă pentru site-ul tău.
            </h2>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
              Ordell WebCare înseamnă mai mult decât actualizări. Înseamnă monitorizare, backup, securitate, verificări funcționale și suport real, astfel încât site-ul tău să rămână util pentru business, nu o problemă amânată.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-[#F0F5F4]/50">
                <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ORDELL */}
      <section data-testid="why-section" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <div className="max-w-3xl mb-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
              Suport clar, procese curate și mai mult control.
            </h2>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
              Noi tratăm site-ul ca pe un activ de business, nu ca pe un task uitat într-un inbox. Asta înseamnă procese clare, comunicare simplă, suport uman și o abordare orientată spre prevenție.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {differentiators.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-['Outfit'] text-lg font-medium text-[#1A1D20] mb-2">{d.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section data-testid="infra-section" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-6">
              <Server className="w-6 h-6 text-[#4A9E96]" />
            </div>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
              Infrastructură serioasă pentru proiectele unde stabilitatea contează.
            </h2>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-4">
              Pentru proiectele unde stabilitatea contează, folosim infrastructură managed sau dedicată, configurată pentru continuitate și o mai bună gestionare a perioadelor de trafic crescut.
            </p>
            <p className="text-sm text-white/60 italic">
              Mai puține limitări tipice hostingului low-cost. Mai multă liniște operațională.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section data-testid="pricing-section" className="py-20 lg:py-28">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-12 text-center">
            Pachete simple, clare și ușor de înțeles.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                data-testid={`pricing-card-${i}`}
                className={`rounded-2xl p-8 relative overflow-hidden transition-all hover:-translate-y-1 ${
                  plan.featured
                    ? "bg-[#0A1118] text-white border border-[#133E59] shadow-lg"
                    : "bg-white text-[#1A1D20] border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                }`}
              >
                {plan.badge && (
                  <span className="inline-block text-[10px] tracking-wider uppercase font-semibold bg-[#1A4A40] text-white px-3 py-1 rounded-full mb-4">
                    {plan.badge}
                  </span>
                )}
                <h3 className="font-['Outfit'] text-xl font-semibold mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="font-['Outfit'] text-3xl font-bold">{plan.price}</span>
                  {plan.period && (
                    <span className={`text-sm ml-1 ${plan.featured ? "text-white/60" : "text-[#4A5568]"}`}>
                      {plan.period}
                    </span>
                  )}
                </div>
                <p className={`text-sm leading-relaxed mb-6 ${plan.featured ? "text-white/70" : "text-[#4A5568]"}`}>
                  {plan.text}
                </p>
                <Link
                  to={plan.href}
                  data-testid={`pricing-cta-${i}`}
                  className={`inline-flex items-center gap-2 font-medium text-sm px-5 py-2.5 rounded-xl transition-all ${
                    plan.featured
                      ? "bg-white text-[#0F2942] hover:bg-white/90"
                      : "bg-[#0F2942] text-white hover:bg-[#0F2942]/90"
                  }`}
                >
                  {plan.cta} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-12 text-center">
            Proces simplu, fără complicații inutile.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((s, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="text-center">
                <span className="font-['Outfit'] text-4xl font-bold text-[#0F2942]/10 block mb-2">{s.num}</span>
                <h3 className="font-['Outfit'] text-lg font-medium text-[#1A1D20] mb-2">{s.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section data-testid="faq-preview-section" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-8">
            Întrebări frecvente
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-xl border border-[#0F2942]/10 px-6 data-[state=open]:shadow-sm"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left text-[#1A1D20] font-medium hover:no-underline py-5"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A5568] leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link
              to="/faq"
              data-testid="faq-see-all-btn"
              className="text-sm font-medium text-[#0F2942] hover:text-[#0F2942]/80 transition-colors inline-flex items-center gap-1"
            >
              Vezi toate întrebările <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
            Lasă site-ul în grija unei echipe care îl tratează ca pe un activ de business.
          </h2>
          <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8">
            Dacă vrei un site WordPress stabil, monitorizat și gestionat corect, discutăm și îți spunem exact ce plan ți se potrivește.
          </p>
          <Link
            to="/contact"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0F2942] font-medium px-8 py-3.5 rounded-xl hover:bg-white/90 transition-all text-base"
          >
            Cere audit gratuit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
